import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plane, FileText, Clock, Calendar, AlertCircle, Phone, MessageCircle, CheckCircle2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [nationalities, setNationalities] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [selectedNationality, setSelectedNationality] = useState('');
  const [selectedVisaType, setSelectedVisaType] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allSpecialRules, setAllSpecialRules] = useState([]);

  useEffect(() => {
    fetchNationalities();
    fetchVisaTypes();
    fetchSpecialRules();
  }, []);

  const fetchSpecialRules = async () => {
    try {
      const response = await axios.get(`${API}/special-rules`);
      setAllSpecialRules(response.data);
    } catch (error) {
      console.error('Error fetching special rules:', error);
    }
  };

  const fetchNationalities = async () => {
    try {
      const response = await axios.get(`${API}/nationalities`);
      setNationalities(response.data);
    } catch (error) {
      console.error('Error fetching nationalities:', error);
      toast.error('Failed to load nationalities');
    }
  };

  const fetchVisaTypes = async () => {
    try {
      const response = await axios.get(`${API}/visa-types`);
      setVisaTypes(response.data);
    } catch (error) {
      console.error('Error fetching visa types:', error);
      toast.error('Failed to load visa types');
    }
  };

  const checkEligibility = async () => {
    if (!selectedNationality || !selectedVisaType) {
      toast.error('Please select both nationality and visa type');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/check-eligibility`, {
        nationality_id: selectedNationality,
        visa_type_id: selectedVisaType
      });
      setResult(response.data);
      toast.success('Eligibility checked successfully');
    } catch (error) {
      console.error('Error checking eligibility:', error);
      toast.error('Failed to check eligibility');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/97142202133?text=Hello%20White%20Sky%20Travel%2C%20I%20want%20to%20apply%20for%20a%20UAE%20visa.', '_blank');
  };

  const handleCall = () => {
    window.location.href = 'tel:+971522921011';
  };

  return (
    <div className="hero-bg min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-3 tracking-tight">
            UAE Visa Eligibility
          </h1>
        </div>

        {/* Calculator Card */}
        <Card className="calculator-card max-w-2xl mx-auto mb-8">
          <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-600 text-white rounded-t-2xl">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl">
              <Plane className="w-6 h-6" />
              Check Your Visa Eligibility
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Nationality Selection */}
              <div>
                <label className="label-text">Select Your Nationality</label>
                <Select onValueChange={setSelectedNationality} value={selectedNationality}>
                  <SelectTrigger data-testid="nationality-selector" className="input-field w-full">
                    <SelectValue placeholder="Choose your nationality" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {nationalities.map((nat) => (
                      <SelectItem key={nat.id} value={nat.id}>
                        {nat.nationality_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Visa Type Selection */}
              <div>
                <label className="label-text">Select Visa Type</label>
                <Select onValueChange={setSelectedVisaType} value={selectedVisaType}>
                  <SelectTrigger data-testid="visa-type-selector" className="input-field w-full">
                    <SelectValue placeholder="Choose visa type" />
                  </SelectTrigger>
                  <SelectContent>
                    {visaTypes.map((visa) => (
                      <SelectItem key={visa.id} value={visa.id}>
                        {visa.visa_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Check Button */}
              <Button
                data-testid="check-eligibility-btn"
                onClick={checkEligibility}
                disabled={loading}
                className="btn-cta w-full"
              >
                {loading ? 'Checking...' : 'Check Eligibility'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="max-w-4xl mx-auto space-y-6 fade-in">
            {result.is_visa_free ? (
              <>
                {/* Visa-Free Entry (GCC Countries) */}
                <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
                        <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-3">
                        No Visa Required - Visa-Free Entry! 🎉
                      </h2>
                      <p className="text-lg text-slate-700 mb-2">
                        Great news! <span className="font-bold text-emerald-600">{result.nationality_name}</span> nationals enjoy visa-free entry to the UAE.
                      </p>
                      <p className="text-slate-600 mb-4">
                        UAE has visa-free entry agreement with Gulf Cooperation Council (GCC) countries.
                      </p>
                      <div className="bg-white rounded-lg p-6 border-2 border-emerald-200 max-w-md mx-auto">
                        <h3 className="font-bold text-slate-900 mb-2">You Can Enter UAE:</h3>
                        <ul className="text-left space-y-2">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>Without applying for a visa</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>Just bring your valid passport</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>No fees or processing required</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Contact Buttons for GCC */}
                    <div className="grid sm:grid-cols-2 gap-4 mt-6">
                      <Button
                        data-testid="gcc-whatsapp-btn"
                        onClick={handleWhatsApp}
                        className="btn-primary h-14 text-lg flex items-center justify-center gap-3"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Contact on WhatsApp
                      </Button>
                      <Button
                        data-testid="gcc-call-btn"
                        onClick={handleCall}
                        className="btn-primary h-14 text-lg flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Phone className="w-5 h-5" />
                        Call Us
                      </Button>
                    </div>

                    <p className="text-sm text-slate-500 mt-4">
                      For any questions or assistance, feel free to contact us
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : result.is_restricted ? (
              <>
                {/* Restricted Visa Type - Contact Us */}
                <Card className="border-2 border-orange-500 bg-gradient-to-br from-orange-50 to-white">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-100 mb-4">
                        <AlertCircle className="w-10 h-10 text-orange-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-3">
                        {result.visa_type} - Special Assistance Required
                      </h2>
                      <p className="text-lg text-slate-700 mb-2">
                        For <span className="font-bold text-orange-600">{result.nationality_name}</span> nationals, we cannot process <span className="font-semibold">{result.visa_type}</span>.
                      </p>
                      <p className="text-slate-600 mb-4">
                        However, we can process <strong>30-day and 60-day tourist visas</strong> for you. Please contact us directly or try selecting a different visa type.
                      </p>

                      {/* Suggestion Box */}
                      {(() => {
                        // Get all restricted visa type IDs for this nationality
                        const restrictedVisaTypeIds = allSpecialRules
                          .filter(rule => 
                            rule.nationality_id === selectedNationality && 
                            rule.is_restricted === true
                          )
                          .map(rule => rule.visa_type_id);
                        
                        // Get available (non-restricted) visa types
                        const availableVisas = visaTypes.filter(
                          visa => !restrictedVisaTypeIds.includes(visa.id)
                        );
                        
                        return availableVisas.length > 0 ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 max-w-md mx-auto">
                            <p className="text-blue-900 font-semibold mb-2">💡 Try These Instead:</p>
                            <ul className="text-left text-blue-800 text-sm space-y-1">
                              {availableVisas.map(visa => (
                                <li key={visa.id}>• {visa.visa_type}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    {/* Contact Information Card */}
                    <Card className="bg-white border-slate-200 mb-6">
                      <CardContent className="p-6">
                        <h3 className="font-bold text-slate-900 mb-4 text-lg">Contact Us for Assistance</h3>
                        <div className="grid md:grid-cols-2 gap-4 text-left">
                          <div className="flex items-start gap-3">
                            <Phone className="w-5 h-5 text-blue-700 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-600">Office Line</p>
                              <a href="tel:+97142202133" className="text-blue-700 font-semibold hover:underline">
                                +971 4 220 2133
                              </a>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Phone className="w-5 h-5 text-emerald-700 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-600">Mobile</p>
                              <a href="tel:+971522921011" className="text-emerald-700 font-semibold hover:underline">
                                +971 52 292 1011
                              </a>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* CTA Buttons */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Button
                        data-testid="restricted-whatsapp-btn"
                        onClick={handleWhatsApp}
                        className="btn-primary h-14 text-lg flex items-center justify-center gap-3"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Contact on WhatsApp
                      </Button>
                      <Button
                        data-testid="restricted-call-btn"
                        onClick={handleCall}
                        className="btn-primary h-14 text-lg flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Phone className="w-5 h-5" />
                        Call Us Now
                      </Button>
                    </div>

                    <p className="text-sm text-slate-500 mt-6">
                      Our team is ready to help you with alternative visa options
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : result.eligible ? (
              <>
                {/* On-Arrival Visa Notice OR Pre-Approved Required */}
                {(result.has_on_arrival_30 || result.has_on_arrival_90 || result.has_on_arrival_180) ? (
                  <Alert className="border-blue-500 bg-blue-50">
                    <AlertCircle className="text-blue-700" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="font-bold text-blue-900 text-lg">ℹ️ On-Arrival Visa Available</p>
                        <p className="text-blue-800">
                          <span className="font-semibold">{result.nationality_name}</span> nationals can get visa on arrival for:
                          <span className="font-bold ml-1">
                            {result.has_on_arrival_30 && '30 days'}
                            {result.has_on_arrival_90 && '90 days'}
                            {result.has_on_arrival_180 && '180 days'}
                          </span>
                        </p>
                        <p className="text-blue-700 text-sm">
                          However, you can also apply for a <strong>pre-approved visa</strong> through us for convenience and guaranteed entry. See details below:
                        </p>
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="border-blue-500 bg-blue-50">
                    <AlertCircle className="text-blue-700" />
                    <AlertDescription>
                      <p className="text-lg font-bold text-blue-900 mb-2">
                        ✓ Pre-Approved Visa Required
                      </p>
                      <p className="text-blue-800">
                        <span className="font-semibold">{result.nationality_name}</span> nationals need a <strong>pre-approved {result.visa_type}</strong> before entering the UAE.
                      </p>
                      <p className="text-blue-700 text-sm mt-2">
                        We can process your visa application. See details below:
                      </p>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Price Card */}
                <Card className="result-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 font-semibold uppercase">Total Visa Price</p>
                        <p className="text-4xl font-bold text-blue-700 mt-1">AED {result.total_price}</p>
                      </div>
                      <div className="bg-blue-100 p-4 rounded-full">
                        <FileText className="w-8 h-8 text-blue-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Details Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="result-card">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5 text-blue-700" />
                        <h3 className="font-bold text-slate-900">Stay Duration</h3>
                      </div>
                      <p className="text-2xl font-semibold text-slate-700">{result.stay_days} Days</p>
                    </CardContent>
                  </Card>

                  <Card className="result-card">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5 text-blue-700" />
                        <h3 className="font-bold text-slate-900">Validity Period</h3>
                      </div>
                      <p className="text-2xl font-semibold text-slate-700">{result.validity_days} Days</p>
                    </CardContent>
                  </Card>

                  <Card className="result-card md:col-span-2">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-blue-700" />
                        <h3 className="font-bold text-slate-900">Processing Time</h3>
                      </div>
                      <p className="text-xl font-semibold text-slate-700">{result.processing_time}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Requirements */}
                <Card className="result-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-700">
                      <CheckCircle2 className="w-5 h-5" />
                      Required Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {result.visa_requirements && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">General Requirements:</h4>
                        <ul className="space-y-2">
                          {result.visa_requirements.split(',').map((req, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="text-blue-700 text-lg mt-0.5">•</span>
                              <span className="text-slate-700 flex-1">{req.trim()}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {result.nationality_requirements && result.nationality_requirements !== 'Standard visa requirements apply' && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Nationality-Specific Requirements:</h4>
                        <ul className="space-y-2">
                          {result.nationality_requirements.split(',').map((req, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="text-orange-600 text-lg mt-0.5">•</span>
                              <span className="text-slate-700 flex-1">{req.trim()}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Special Rules */}
                {result.special_rules && result.special_rules.length > 0 && (
                  <Card className="result-card border-orange-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-orange-600">
                        <AlertCircle className="w-5 h-5" />
                        Special Requirements
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {result.special_rules.map((rule, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-orange-600 mt-1">•</span>
                            <span className="text-slate-700">{rule}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                {(result.visa_notes || result.nationality_notes) && (
                  <Card className="result-card">
                    <CardHeader>
                      <CardTitle className="text-slate-900">Additional Notes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {result.visa_notes && <p className="text-slate-700">{result.visa_notes}</p>}
                      {result.nationality_notes && <p className="text-slate-700">{result.nationality_notes}</p>}
                    </CardContent>
                  </Card>
                )}

                {/* CTA Buttons */}
                <div className="grid sm:grid-cols-2 gap-4 pt-4">
                  <Button
                    data-testid="whatsapp-btn"
                    onClick={handleWhatsApp}
                    className="btn-primary h-14 text-lg flex items-center justify-center gap-3"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Apply on WhatsApp
                  </Button>
                  <Button
                    data-testid="call-btn"
                    onClick={handleCall}
                    className="btn-primary h-14 text-lg flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Phone className="w-5 h-5" />
                    Call Us Now
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Not Eligible - Contact Us Section */}
                <Card className="border-2 border-orange-500 bg-gradient-to-br from-orange-50 to-white">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-100 mb-4">
                        <AlertCircle className="w-10 h-10 text-orange-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-3">
                        Visa Processing Requires Special Assistance
                      </h2>
                      <p className="text-lg text-slate-700 mb-2">
                        For <span className="font-bold text-orange-600">{result.nationality_name}</span> nationals, UAE visa applications require additional documentation and special processing.
                      </p>
                      <p className="text-slate-600">
                        Please contact our visa specialists directly for personalized assistance and detailed requirements.
                      </p>
                    </div>

                    {/* Contact Information Card */}
                    <Card className="bg-white border-slate-200 mb-6">
                      <CardContent className="p-6">
                        <h3 className="font-bold text-slate-900 mb-4 text-lg">Get Expert Assistance</h3>
                        <div className="grid md:grid-cols-2 gap-4 text-left">
                          <div className="flex items-start gap-3">
                            <Phone className="w-5 h-5 text-blue-700 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-600">Office Line</p>
                              <a href="tel:+97142202133" className="text-blue-700 font-semibold hover:underline">
                                +971 4 220 2133
                              </a>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Phone className="w-5 h-5 text-emerald-700 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-600">Mobile</p>
                              <a href="tel:+971522921011" className="text-emerald-700 font-semibold hover:underline">
                                +971 52 292 1011
                              </a>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* CTA Buttons */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Button
                        data-testid="contact-whatsapp-btn"
                        onClick={handleWhatsApp}
                        className="btn-primary h-14 text-lg flex items-center justify-center gap-3"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Contact on WhatsApp
                      </Button>
                      <Button
                        data-testid="contact-call-btn"
                        onClick={handleCall}
                        className="btn-primary h-14 text-lg flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Phone className="w-5 h-5" />
                        Call Us Now
                      </Button>
                    </div>

                    <p className="text-sm text-slate-500 mt-6">
                      Our visa experts are available to guide you through the entire process
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-slate-600 space-y-2">
          <p className="text-sm">© 2025 White Sky Travel. All rights reserved.</p>
          <p className="text-xs">Contact: +971 4 220 2133 | +971 52 292 1011</p>
          <div className="flex items-center justify-center gap-4 text-xs flex-wrap">
            <a 
              href="https://whitesky.ae" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-700 hover:text-blue-800 font-medium hover:underline"
            >
              www.whitesky.ae
            </a>
            <span>•</span>
            <a 
              href="https://maps.app.goo.gl/sFpWU924aC23FbCT7" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-700 hover:text-blue-800 font-medium hover:underline flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              Office Location
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
